import React, { useState, useEffect } from 'react';
import { Flame, X, Zap, Trophy, Star, Target } from 'lucide-react';
import { calculateStreak, type StreakData } from '../utils/streak';
import { useTranslation } from '../utils/translations';
import { sendPushNotification, checkShouldSendNotification } from '../utils/pushNotifications';

interface StreakNotificationProps {
  userId: string;
  nativeLanguage: string;
  onClose: () => void;
  showOnlyIfStreak?: boolean;
}

export function StreakNotification({ userId, nativeLanguage, onClose, showOnlyIfStreak = false }: StreakNotificationProps) {
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { t, isRTL } = useTranslation(nativeLanguage);

  useEffect(() => {
    loadStreak();
  }, [userId, nativeLanguage]);

  const loadStreak = async () => {
    const data = await calculateStreak(userId, nativeLanguage);
    setStreakData(data);

    if (data.streakAtRisk) {
      const shouldSend = await checkShouldSendNotification(userId, 'streak_reminder');
      if (shouldSend && data.currentStreak > 0) {
        await sendPushNotification(
          userId,
          'Streak at Risk!',
          `Your ${data.currentStreak}-day streak is at risk. Practice today to keep it going!`
        );
      }
    }

    if (showOnlyIfStreak && data.currentStreak === 0 && !data.streakAtRisk) {
      onClose();
      return;
    }

    setTimeout(() => {
      setIsVisible(true);
      setIsAnimating(true);
    }, 100);
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!streakData) return null;

  const { currentStreak, longestStreak, todayCompleted, streakAtRisk } = streakData;

  const getBackgroundGradient = () => {
    if (currentStreak === 0) return 'from-gray-500 to-gray-600';
    if (streakAtRisk) return 'from-amber-500 to-orange-600';
    if (currentStreak < 7) return 'from-orange-500 to-red-500';
    if (currentStreak < 30) return 'from-orange-400 to-rose-500';
    if (currentStreak < 100) return 'from-rose-500 to-pink-600';
    return 'from-amber-400 via-orange-500 to-red-600';
  };

  const getIcon = () => {
    if (currentStreak === 0) return <Target className="h-8 w-8 text-white" />;
    if (currentStreak < 7) return <Flame className="h-8 w-8 text-white" />;
    if (currentStreak < 30) return <Zap className="h-8 w-8 text-white" />;
    if (currentStreak < 100) return <Star className="h-8 w-8 text-white" />;
    return <Trophy className="h-8 w-8 text-white" />;
  };

  const renderFlames = () => {
    if (currentStreak === 0) return null;

    const flameCount = Math.min(currentStreak, 7);
    return (
      <div className="flex justify-center gap-1 mt-2">
        {Array.from({ length: flameCount }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse"
            style={{
              animationDelay: `${i * 100}ms`,
              animationDuration: '1s'
            }}
          >
            <Flame
              className={`h-5 w-5 ${
                i < 3 ? 'text-yellow-300' : i < 5 ? 'text-orange-300' : 'text-red-300'
              }`}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderStreakRing = () => {
    const progress = Math.min((currentStreak / 7) * 100, 100);
    const circumference = 2 * Math.PI * 45;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="relative w-28 h-28 mx-auto">
        <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="white"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
            style={{
              filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.5))'
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-white drop-shadow-lg">
            {currentStreak}
          </span>
          <span className="text-xs text-white/80 font-medium">
            {currentStreak === 1 ? t('day') : t('days')}
          </span>
        </div>
      </div>
    );
  };

  const getTitle = () => {
    if (currentStreak === 0) return t('Start Your Journey!');
    if (streakAtRisk) return t('Keep It Going!');
    if (todayCompleted) return t('Amazing Work!');
    return t('Welcome Back!');
  };

  const getStreakMessageTranslated = () => {
    if (currentStreak === 0 && !todayCompleted) {
      return t('Start your streak today!');
    }
    if (streakAtRisk) {
      return isRTL
        ? `${currentStreak} ${t('day streak')} - ${t('Practice today to keep it going!')}`
        : `${currentStreak} ${t('day streak')} - ${t('Practice today to keep it going!')}`;
    }
    if (currentStreak === 1) {
      return t('You started a streak! Keep it going tomorrow!');
    }
    if (currentStreak < 7) {
      return isRTL
        ? `${currentStreak} ${t('day streak')} - ${t("You're building momentum!")}`
        : `${currentStreak} ${t('day streak')} - ${t("You're building momentum!")}`;
    }
    if (currentStreak < 30) {
      return isRTL
        ? `${currentStreak} ${t('day streak')} - ${t('Amazing dedication!')}`
        : `${currentStreak} ${t('day streak')} - ${t('Amazing dedication!')}`;
    }
    if (currentStreak < 100) {
      return isRTL
        ? `${currentStreak} ${t('day streak')} - ${t("You're unstoppable!")}`
        : `${currentStreak} ${t('day streak')} - ${t("You're unstoppable!")}`;
    }
    return isRTL
      ? `${currentStreak} ${t('day streak')} - ${t('Legendary learner!')}`
      : `${currentStreak} ${t('day streak')} - ${t('Legendary learner!')}`;
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className={`relative w-full max-w-sm transform transition-all duration-500 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <div
          className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${getBackgroundGradient()} p-6 shadow-2xl`}
        >
          <div className="absolute inset-0 overflow-hidden">
            <div className={`absolute -top-10 ${isRTL ? '-left-10' : '-right-10'} w-40 h-40 bg-white/10 rounded-full blur-2xl`} />
            <div className={`absolute -bottom-10 ${isRTL ? '-right-10' : '-left-10'} w-40 h-40 bg-white/10 rounded-full blur-2xl`} />
            {currentStreak > 0 && (
              <>
                <div
                  className={`absolute top-20 ${isRTL ? 'right-10' : 'left-10'} w-2 h-2 bg-yellow-300 rounded-full animate-ping`}
                  style={{ animationDuration: '2s' }}
                />
                <div
                  className={`absolute top-32 ${isRTL ? 'left-16' : 'right-16'} w-1.5 h-1.5 bg-white rounded-full animate-ping`}
                  style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}
                />
                <div
                  className={`absolute bottom-24 ${isRTL ? 'right-20' : 'left-20'} w-1 h-1 bg-yellow-200 rounded-full animate-ping`}
                  style={{ animationDuration: '3s', animationDelay: '1s' }}
                />
              </>
            )}
          </div>

          <button
            onClick={handleClose}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors z-10`}
            type="button"
          >
            <X className="h-5 w-5 text-white" />
          </button>

          <div className="relative text-center">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4 ${
                isAnimating && currentStreak > 0 ? 'animate-bounce' : ''
              }`}
              style={{ animationDuration: '1s', animationIterationCount: '2' }}
            >
              {getIcon()}
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">
              {getTitle()}
            </h2>

            {renderStreakRing()}

            <p className="text-white/90 mt-4 text-lg font-medium">
              {getStreakMessageTranslated()}
            </p>

            {renderFlames()}

            {longestStreak > currentStreak && longestStreak > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-white/70 text-sm">
                  {t('Your best:')} <span className="font-semibold text-white">{longestStreak} {t('days')}</span>
                </p>
              </div>
            )}

            {streakAtRisk && (
              <div className="mt-4 p-3 bg-white/20 rounded-xl">
                <p className="text-white text-sm font-medium">
                  {t('Complete a lesson today to maintain your streak!')}
                </p>
              </div>
            )}

            <button
              onClick={handleClose}
              type="button"
              className="mt-6 w-full py-3 bg-white/20 hover:bg-white/30 rounded-xl text-white font-semibold transition-colors"
            >
              {currentStreak === 0 ? t("Let's Go!") : t('Continue Learning')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface StreakBadgeProps {
  userId: string;
  nativeLanguage: string;
  onClick?: () => void;
}

export function StreakBadge({ userId, nativeLanguage, onClick }: StreakBadgeProps) {
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadStreak();
    }
  }, [userId, nativeLanguage]);

  const loadStreak = async () => {
    setLoading(true);
    try {
      const data = await calculateStreak(userId, nativeLanguage);
      setStreakData(data);
    } catch (err) {
      console.error('StreakBadge: Error loading streak:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !streakData) return null;

  const { currentStreak, streakAtRisk } = streakData;

  if (currentStreak === 0) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105 bg-gray-100 text-gray-600 hover:bg-gray-200"
      >
        <Flame className="h-4 w-4 text-gray-400" />
        <span>0</span>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-all hover:scale-105 ${
        streakAtRisk
          ? 'bg-amber-100 text-amber-700 animate-pulse'
          : 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md'
      }`}
    >
      <Flame className={`h-4 w-4 ${streakAtRisk ? 'text-amber-600' : 'text-white'}`} />
      <span>{currentStreak}</span>
    </button>
  );
}
